

import React, { memo } from "react";
import { useTranslation } from "react-i18next";

function DataTablePaginationComponent({
  currentPage,
  onChangePage,
  onChangeRowsPerPage,
  rowCount,
  rowsPerPage,
  paginationRowsPerPageOptions,
}) {
  const { t } = useTranslation(["pagination", "common"]);
  const safeRowsPerPage =
    Number(rowsPerPage) > 0 && !Number.isNaN(Number(rowsPerPage))
      ? Number(rowsPerPage)
      : 10;
  const safeRowCount =
    Number(rowCount) >= 0 && !Number.isNaN(Number(rowCount))
      ? Number(rowCount)
      : 0;
  const safeCurrentPage =
    Number(currentPage) > 0 && !Number.isNaN(Number(currentPage))
      ? Number(currentPage)
      : 1;
  const totalPages =
    safeRowsPerPage > 0 ? Math.ceil(safeRowCount / safeRowsPerPage) : 0;
  const startRow =
    safeRowCount === 0 ? 0 : (safeCurrentPage - 1) * safeRowsPerPage + 1;
  const endRow =
    safeRowCount === 0
      ? 0
      : Math.min(safeRowCount, safeCurrentPage * safeRowsPerPage);

  const paginationOptions = {
    perPage: safeRowsPerPage,
    pageNumber: safeCurrentPage,
    totalItems: safeRowCount,
  };

  const generatePaginationNumbers = () => {
    const { perPage, pageNumber, totalItems } = paginationOptions;
    const pageCount = Math.ceil(totalItems / perPage);

    if (!pageCount || pageCount <= 0 || Number.isNaN(pageCount)) return [];

    const startPage = Math.max(1, pageNumber - 1);
    const endPage = Math.min(pageCount, pageNumber + 1);
    const firstIndex = 1;
    const lastIndex = pageCount;

    const paginationNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      paginationNumbers.push(i);
    }

    if (firstIndex + 1 < endPage && !paginationNumbers.includes(firstIndex + 1))
      paginationNumbers.unshift("...");
    if (!paginationNumbers.includes(firstIndex)) paginationNumbers.unshift(1);

    if (lastIndex - 1 > startPage && !paginationNumbers.includes(lastIndex - 1))
      paginationNumbers.push("...");
    if (!paginationNumbers.includes(lastIndex))
      paginationNumbers.push(pageCount);

    return paginationNumbers;
  };

  return (
    <div className="dataTablePagination">
      <style>{`
        .dataTablePagination {
          container-type: inline-size;
          container-name: dataTablePagination;
        }
        .dataTablePagination .show {
          display: inline;
        }
        @container dataTablePagination (max-width: 450px) {
          .dataTablePagination .show {
            display: none !important;
          }
        }
      `}</style>

      <div className="d-flex gap-1">
        <p className="show">
          {t("rowsPerPage", { ns: "pagination", defaultValue: "Rows per page" })}
        </p>
        <select
          className="perPage"
          onChange={(e) => onChangeRowsPerPage(+e.target.value)}
          value={safeRowsPerPage}
        >
          {paginationRowsPerPageOptions.map((rows) => (
            <option value={rows} key={rows}>
              {rows}
            </option>
          ))}
        </select>
      </div>

      <p>
        <span className="show">
          {t("showing", { ns: "pagination", defaultValue: "Rows" })}{" "}
        </span>
        {startRow}-{endRow}{" "}
        {t("of", { ns: "pagination", defaultValue: "of" })} {safeRowCount}
      </p>

      <div className="buttons">
        <button
          className="btn"
          disabled={safeCurrentPage <= 1 || totalPages === 0}
          onClick={() => onChangePage(safeCurrentPage - 1)}
        >
          {t("prev", { ns: "pagination", defaultValue: "Prev" })}
        </button>
        {generatePaginationNumbers().map((num, index) => {
          if (typeof num === "string")
            return (
              <p className="show" key={num + index}>
                {num}
              </p>
            );
          return (
            <button
              type="button"
              className={`btn show${
                Number(paginationOptions.pageNumber) === Number(num)
                  ? " selected"
                  : ""
              }`}
              key={num}
              onClick={() => onChangePage(num)}
              aria-current={
                Number(paginationOptions.pageNumber) === Number(num)
                  ? "page"
                  : undefined
              }
            >
              {num}
            </button>
          );
        })}
        <button
          className="btn"
          disabled={safeCurrentPage >= totalPages || totalPages === 0}
          onClick={() => onChangePage(safeCurrentPage + 1)}
        >
          {t("next", { ns: "pagination", defaultValue: "Next" })}
        </button>
      </div>
    </div>
  );
}

export default memo(DataTablePaginationComponent);
